import { fileHref, FileImg } from '../lib/fileAccess'
import { expiryStatus, expiryToday, storedStatus } from '../lib/expiry'
import { useEffect, useState, Fragment } from 'react'
import { createPortal } from 'react-dom'
import DatePicker, { fmtDocDate } from '../components/DatePicker'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import html2pdf from 'html2pdf.js'
import { fetchRows, createRow, updateRow } from '../lib/api'
import DateField from '../components/DateField'
import Modal from '../components/Modal'
import HistoryLink from '../components/HistoryLink'
import { useAuth } from '../hooks/useAuth'
import { filePath } from '../lib/filePaths'
import { formBody } from '../lib/formBody'
import { readDocWithAi, fillFromAi, filledNote, CREW_CERT_FIELDS } from '../lib/docRead'
import { findReplacement, replaceLabel, NEW_LABEL } from '../lib/certTypes'
import { canSeeOthers, isOwnCrew } from '../lib/crewPrivacy'
import { askWriteHandover } from './Handover'
import { rotationPeriods } from '../lib/rotationPeriods'
import { runSignEvent, undoSignEvent } from '../lib/signEventIo'
import CrewDataPanel from '../components/CrewDataPanel'
import { canManageCrewData, eraseReminder, ERASE_AFTER_MONTHS } from '../lib/crewData'

const DEPT_COLORS = {
  'Engineering': { bg: 'rgba(52,120,181,.15)', tc: '#2a6a96' },
  'Deck':        { bg: 'rgba(61,158,114,.15)', tc: '#137050' },
  'Interior':    { bg: 'rgba(167,107,237,.15)', tc: '#6b3fb0' },
  'Exterior':    { bg: 'rgba(201,147,58,.15)',  tc: 'var(--gold2)' },
  'Management':  { bg: 'rgba(192,67,74,.15)',   tc: '#b4313c' },
}

const FORM_FIELDS = [
  { name: 'Crew ID',             label: 'Crew ID',              placeholder: 'e.g. CM-009' },
  { name: 'First Name',          label: 'First Name',           required: true },
  { name: 'Last Name',           label: 'Last Name',            required: true },
  { name: 'Position',            label: 'Position/Rank',        placeholder: 'e.g. Chief Engineer' },
  { name: 'Department',          label: 'Department',           type: 'select', options: ['Engineering','Deck','Interior','Exterior','Management'] },
  { name: 'Nationality',         label: 'Nationality' },
  { name: 'Date of Birth',       label: 'Date of Birth',        type: 'date' },
  { name: 'Place of Birth',      label: 'Place of Birth' },
  { name: 'Phone',               label: 'Phone' },
  { name: 'Email',               label: 'Email',                type: 'email' },
  { name: 'Contract Start Date', label: 'Contract Start',       type: 'date' },
  { name: 'Contract End Date',   label: 'Contract End',         type: 'date' },
  { name: 'Leave Entitlement Days', label: 'Leave Entitlement (days/yr)', type: 'number', placeholder: 'e.g. 120' },
  { name: 'Onboard',             label: 'Onboard Status',       type: 'select', options: ['true','false'] },
  { name: 'Status',              label: 'Status',               type: 'select', options: ['Active','Ashore','Signed Off','Off Duty'] },
  { name: 'Passport No',         label: 'Passport No' },
  { name: 'Passport Issue Place',label: 'Passport Issue Place' },
  { name: 'Passport Issued Date',label: 'Passport Issued',      type: 'date' },
  { name: 'Passport Expiry Date',label: 'Passport Expiry',      type: 'date' },
  { name: "Seaman's Book No",    label: "Seaman's Book No" },
  { name: "Seaman's Book Expiry Date", label: "Seaman's Book Expiry", type: 'date' },
  { name: 'Emergency Contact Name',  label: 'Emergency Contact Name' },
  { name: 'Emergency Contact Phone', label: 'Emergency Contact Phone' },
  { name: 'Photo URL',           label: 'Photo URL',            placeholder: 'https://...' },
  { name: 'Notes',               label: 'Notes',                type: 'textarea' },
]
// the form fields stored in crew_private (src/lib/crewPrivacy.js PERSONAL_FIELDS, as the page names them)
const PERSONAL_FORM = ['Nationality', 'Date of Birth', 'Place of Birth', 'Phone', 'Email', 'Passport No', 'Passport Issue Place', 'Passport Issued Date',
  'Passport Expiry Date', "Seaman's Book No", "Seaman's Book Expiry Date", 'Emergency Contact Name', 'Emergency Contact Phone', 'Notes']

const LOG_STATUSES = ['On Rotation','Off Rotation','On Vacation','Compassionate Leave','Unpaid Leave','Shoreside Training','Medical Leave','Yacht Business Leave','Not Aboard','Paid Time Off','Paid Travel Days','Neutral Travel Days','Unpaid Travel Days','Paid Quarantine Days','Neutral Quarantine Days','Unpaid Quarantine Days']

function getVal(v) {
  if (!v) return ''
  if (typeof v === 'object' && v.value) return v.value
  return String(v)
}

// the shared expiry rule (src/lib/expiry.js): valid THROUGH the expiry date (ship's time); the crew card warns 90 days ahead
function isExpiringSoon(dateStr) {
  return !!dateStr && expiryStatus(dateStr, expiryToday(), { soonDays: 90 }).soon
}

function isExpired(dateStr) {
  return !!dateStr && expiryStatus(dateStr, expiryToday()).expired
}

function dateColor(dateStr) {
  if (!dateStr) return 'var(--text)'
  if (isExpired(dateStr)) return '#b4313c'
  if (isExpiringSoon(dateStr)) return 'var(--gold2)'
  return 'var(--text)'
}

function isOnboard(crew) {
  return crew['Onboard'] === true || crew['Onboard'] === 'true'
}

function docStatus(crew) {
  const passport = getVal(crew['Passport Expiry Date'])
  const seamans = getVal(crew["Seaman's Book Expiry"])
  const contract = getVal(crew['Contract End Date'])
  if (isExpired(passport) || isExpired(seamans) || isExpired(contract)) return 'expired'
  if (isExpiringSoon(passport) || isExpiringSoon(seamans) || isExpiringSoon(contract)) return 'expiring'
  return 'ok'
}

function Avatar({ name, photo, size = 48 }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()
  if (photo) return <FileImg src={photo} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border2)', flexShrink: 0 }} />
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--chip)', border: '1.5px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.28, fontWeight: 600, color: 'var(--gold2)', flexShrink: 0 }}>
      {initials}
    </div>
  )
}

function OnboardSlider({ value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, cursor: 'pointer' }} onClick={onChange}>
      <span style={{ fontSize: 9, color: value ? 'var(--muted)' : '#b4313c', fontWeight: value ? 400 : 600 }}>No</span>
      <div style={{ width: 32, height: 17, borderRadius: 9, background: value ? '#137050' : 'rgba(255,255,255,.1)', border: '0.5px solid var(--border2)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: value ? 16 : 2, width: 11, height: 11, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
      </div>
      <span style={{ fontSize: 9, color: value ? '#137050' : 'var(--muted)', fontWeight: value ? 600 : 400 }}>Yes</span>
    </div>
  )
}

function CrewCard({ crew, onEdit, onToggleOnboard, onClick }) {
  const name = `${getVal(crew['First Name'])} ${getVal(crew['Last Name'])}`
  const dept = getVal(crew['Department'])
  const deptStyle = DEPT_COLORS[dept] || { bg: 'rgba(255,255,255,.06)', tc: 'var(--muted)' }
  const ob = isOnboard(crew)
  const contractEnd = getVal(crew['Contract End Date'])
  const passportExpiry = getVal(crew['Passport Expiry Date'])
  const ds = docStatus(crew)

  return (
    <div style={cs.card} onClick={onClick} data-crew-card={crew.id}>
      <div style={cs.cardHeader}>
        <Avatar name={name} photo={getVal(crew['Photo URL'])} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={cs.crewName}>{name}</div>
          {getVal(crew['Position']) && <div style={cs.crewPos}>{getVal(crew['Position'])}</div>}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 3 }}>
            {dept && <span style={{ ...cs.deptBadge, background: deptStyle.bg, color: deptStyle.tc }}>{dept}</span>}
            <span style={{ ...cs.deptBadge, background: ds === 'ok' ? 'rgba(61,158,114,.15)' : ds === 'expiring' ? 'rgba(201,147,58,.15)' : 'rgba(192,67,74,.15)', color: ds === 'ok' ? '#137050' : ds === 'expiring' ? 'var(--gold2)' : '#b4313c' }}>
              {ds === 'ok' ? '✓ Docs OK' : ds === 'expiring' ? '⚠ Expiring' : '✕ Expired'}
            </span>
          </div>
        </div>
        <div onClick={e => e.stopPropagation()}>
          <OnboardSlider value={ob} onChange={() => onToggleOnboard(crew, ob)} />
        </div>
      </div>
      <div style={cs.cardBody}>
        {getVal(crew['Nationality']) && <div style={cs.infoRow}><span style={cs.infoLabel}>Nationality</span><span style={cs.infoVal}>{getVal(crew['Nationality'])}</span></div>}
        {getVal(crew['Phone']) && <div style={cs.infoRow}><span style={cs.infoLabel}>Phone</span><span style={cs.infoVal}>{getVal(crew['Phone'])}</span></div>}
        {passportExpiry && <div style={cs.infoRow}><span style={cs.infoLabel}>Passport Exp.</span><span style={{ ...cs.infoVal, color: dateColor(passportExpiry) }}>{passportExpiry}{isExpired(passportExpiry) ? ' ⚠' : isExpiringSoon(passportExpiry) ? ' ⚠' : ''}</span></div>}
      </div>
      <div style={cs.cardFooter}>
        <button className="act-btn" style={{ flex: 1 }} onClick={e => { e.stopPropagation(); onEdit(crew) }}>Edit</button>
        <button className="act-btn" style={{ flex: 1 }} onClick={e => { e.stopPropagation(); onClick() }}>View</button>
      </div>
    </div>
  )
}

function LogEntryModal({ name, onClose, onSave }) {
  const [form, setForm] = useState({ 'Date From': new Date().toISOString().split('T')[0] })
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try { await onSave(form); onClose() }
    catch(e) { alert('Save failed: ' + e.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--ink2)', border: '0.5px solid var(--border2)', borderRadius: 14, padding: 24, width: 400, maxHeight: '80vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Add Log Entry — {name}</div>
        {[
          { name: 'Action', label: 'Action', type: 'select', options: ['Signed On','Signed Off'] },
          { name: 'Date From', label: 'Date From', type: 'date' },
          { name: 'Date To', label: 'Date To', type: 'date' },
          { name: 'Employment Status', label: 'Employment Status', type: 'select', options: LOG_STATUSES },
          { name: 'Port', label: 'Port' },
          { name: 'Log Entry', label: 'Log Entry' },
          { name: 'Notes', label: 'Notes' },
        ].map(f => (
          <div key={f.name} className="field">
            <label>{f.label}</label>
            {f.type === 'select'
              ? <select value={form[f.name] || ''} onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))}><option value="">Select...</option>{f.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
              : <input type={f.type || 'text'} value={form[f.name] || ''} onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))} />
            }
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

// Sign on / sign off: asks the date (today by default, any day for a late entry) and the place, then runs the ONE sign-on/off
// function (flag + Crew Log line + rotation). Undo for 10 seconds.
function SignEventModal({ crew, signingOn, onClose, onCrewChanged }) {
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)
  const [port, setPort] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)
  const [left, setLeft] = useState(10)
  const nm = (getVal(crew['First Name']) + ' ' + getVal(crew['Last Name'])).trim()

  useEffect(() => {
    if (!done) return
    if (left <= 0) { onClose(); return }
    const t = setTimeout(() => setLeft(l => l - 1), 1000)
    return () => clearTimeout(t)
  }, [done, left])

  async function save() {
    if (!date) { setError('Pick the date.'); return }
    setSaving(true); setError('')
    try { setDone(await runSignEvent(crew, { signingOn, date, port: port.trim() }, onCrewChanged)) }
    catch (e) { setError('Could not save: ' + (e.message || e)) }
    finally { setSaving(false) }
  }
  async function undo() {
    setSaving(true)
    try { await undoSignEvent(done.io, done.undo); onClose() }
    catch (e) { setError('Could not undo: ' + (e.message || e)); setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={done ? onClose : undefined}>
      <div style={{ background: 'var(--ink2)', border: '0.5px solid var(--border2)', borderRadius: 14, padding: 22, width: 340, display: 'flex', flexDirection: 'column', gap: 12 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{nm}: {signingOn ? 'sign on' : 'sign off'}</div>
        {!done ? (<>
          <div className="field"><label>{signingOn ? 'Joined on' : 'Went ashore on'}</label><DateField value={date} onChange={v => setDate(v || '')} /></div>
          <div className="field"><label>Place (optional)</label><input value={port} onChange={e => setPort(e.target.value)} placeholder="e.g. Antibes" /></div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{signingOn ? 'The open leave ends the day before; Working starts on this date.' : 'The open Working period ends on this date; Leave starts the next day.'} Pick another day for a late entry.</div>
          {error && <div style={{ fontSize: 12, color: '#b4313c' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : (signingOn ? 'Sign on' : 'Sign off')}</button>
          </div>
        </>) : (<>
          <div style={{ fontSize: 12, color: 'var(--text)' }}>{signingOn ? 'Signed on' : 'Signed off'} {date}. Crew Log and rotation updated.</div>
          {done.done.map((d, i) => <div key={i} style={{ fontSize: 11, color: 'var(--muted)' }}>· {d}</div>)}
          {done.warn.map((d, i) => <div key={'w' + i} style={{ fontSize: 11, color: '#e8b96a' }}>· {d}</div>)}
          {error && <div style={{ fontSize: 12, color: '#b4313c' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={undo} disabled={saving}>Undo ({left}s)</button>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </>)}
      </div>
    </div>
  )
}

function DocModal({ doc, prefill, onClose, onSave, onDelete, crewName, existing }) {
  const [form, setForm] = useState({
    'Title': prefill?.['Title'] || doc?.['Title'] || '',
    'Type': prefill?.['Type'] || (doc ? getVal(doc['Type']) : ''),
    'Doc ID': prefill?.['Doc ID'] || doc?.['Doc ID'] || '',
    'Issued Date': prefill?.['Issued Date'] || doc?.['Issued Date'] || '',
    'Expiry Date': prefill?.['Expiry Date'] || doc?.['Expiry Date'] || '',
    'File URL': prefill?.['File URL'] || doc?.['File URL'] || '',
    'Notes': prefill?.['Notes'] || doc?.['Notes'] || '',
  })
  const [saving, setSaving] = useState(false)
  const [aiNote, setAiNote] = useState('')
  const [uploading, setUploading] = useState(false)
  const { config } = useAuth()

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // "Read with AI": the certificate reader fills Title, Type, number, dates, Notes (only empty fields when it runs by itself after an upload)
  async function readAi(url, overwrite) {
    setAiNote('Reading the certificate with AI…')
    try {
      const ai = await readDocWithAi(url, 'crew', config?.id)
      setForm(f => { const r = fillFromAi(f, ai, CREW_CERT_FIELDS, { overwrite }); setAiNote(filledNote(r.filled)); return r.form })
    } catch (e) { setAiNote('AI could not read it: ' + e.message) }
  }
  async function uploadPdf(e) {
    const file = e.target.files && e.target.files[0]; e.target.value = ''
    if (!file) return
    if (file.size > 18 * 1024 * 1024) { alert('File too large. Please upload a PDF under 18MB.'); return }
    setUploading(true)
    try {
      const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = () => rej(new Error('Read failed')); r.readAsDataURL(file) })
      const up = await fetch('/api/upload-cert', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base64, filename: file.name, area: 'crew_certs', client_id: config?.id, mimeType: file.type }) })
      const { url, error } = await up.json().catch(() => ({}))
      if (!url) throw new Error(error || 'upload failed')
      set('File URL', url)
      setUploading(false)
      readAi(url, false)   // once, by itself, right after the upload
    } catch (err) { alert('Upload failed: ' + err.message) } finally { setUploading(false) }
  }

  // a new certificate replaces one on file ONLY when it is the same kind for this person (src/lib/certTypes.js)
  const match = !doc ? findReplacement((existing || []).map(d => ({ id: d.id, title: d['Title'] || '', type: getVal(d['Type']) || '', expiry_date: d['Expiry Date'] || '' })), crewName, { title: form['Title'], type: form['Type'] }) : null
  const [keepBoth, setKeepBoth] = useState(false)
  async function handleSubmit() {
    setSaving(true)
    try { await onSave(form, match && !keepBoth ? match.id : null) } catch(e) { console.error(e) }
    finally { setSaving(false) }
  }

  const inputStyle = { width: '100%', background: 'var(--hull)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }
  const labelStyle = { fontSize: 11, color: 'var(--muted)', marginBottom: 4, display: 'block' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--ink2)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 24, width: 400, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{doc ? 'Edit Document' : 'Add Document'}</div>
        {!doc && (form['Title'] || form['Type']) && (match
          ? <div style={{ fontSize: 12, padding: '8px 10px', borderRadius: 8, border: '0.5px solid var(--gold)', background: 'rgba(201,168,76,.08)' }}>
              <b style={{ color: 'var(--gold2)' }}>{keepBoth ? NEW_LABEL : replaceLabel(match, form['Expiry Date'])}</b>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, color: 'var(--muted)' }}><input type="checkbox" checked={keepBoth} onChange={e => setKeepBoth(e.target.checked)} />Keep both (save as a new record)</label>
            </div>
          : <div style={{ fontSize: 12, padding: '8px 10px', borderRadius: 8, border: '0.5px solid var(--border)' }}><b>{NEW_LABEL}</b> <span style={{ color: 'var(--muted)' }}>- no certificate of this kind on file for {crewName}</span></div>)}

        <div>
          <label style={labelStyle}>Title</label>
          <input style={inputStyle} value={form['Title']} onChange={e => set('Title', e.target.value)} placeholder="e.g. Passport" />
        </div>

        <div>
          <label style={labelStyle}>Type</label>
          <select style={inputStyle} value={form['Type']} onChange={e => set('Type', e.target.value)}>
            <option value="">— Select type —</option>
            {['Passport','Medical Certificate','STCW','Competency Certificate','Visa',"Seaman's Book",'Other'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Doc ID</label>
          <input style={inputStyle} value={form['Doc ID']} onChange={e => set('Doc ID', e.target.value)} placeholder="e.g. PP-2020-001" />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Issued Date</label>
            <DateField value={form['Issued Date'] || ''} onChange={v => set('Issued Date', v || '')} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Expiry Date</label>
            <DateField value={form['Expiry Date'] || ''} onChange={v => set('Expiry Date', v || '')} style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>File URL</label>
          <input style={inputStyle} value={form['File URL']} onChange={e => set('File URL', e.target.value)} placeholder="https://..." />
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="act-btn" style={{ cursor: uploading ? 'wait' : 'pointer', fontSize: 12 }}>{uploading ? 'Uploading…' : '📎 Upload PDF'}<input type="file" accept="application/pdf,image/*" disabled={uploading} style={{ display: 'none' }} onChange={uploadPdf} /></label>
            {form['File URL'] && <button className="act-btn" style={{ fontSize: 12 }} disabled={/^Reading/.test(aiNote)} onClick={() => readAi(form['File URL'], true)}>✨ Read with AI</button>}
          </div>
          {aiNote && <div style={{ fontSize: 12, color: /could not/.test(aiNote) ? '#b4313c' : 'var(--muted)', marginTop: 6 }}>{aiNote}</div>}
        </div>

        <div>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form['Notes']} onChange={e => set('Notes', e.target.value)} placeholder="Any notes..." />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4, alignItems: 'center' }}>
          <div>
            {doc && <button className="act-btn" style={{ color: '#b4313c', fontSize: 11 }} onClick={() => onDelete(doc)}>Delete</button>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="act-btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailPanel({ crew, onClose, onEdit, onToggleOnboard, onUpdateCrew, onCrewChanged }) {
  // the vessel being viewed (management: the selected vessel) - the certificate upload sends its id
  const { config, can } = useAuth()
  // "Write my handover" (ai-next part 1): on the record of the login going off - a crew login's own record, or the Captain's
  // record for the vessel account - when the login may use the assistant and view Rotations
  const ownForHandover = (config?.accountType === 'crew' ? isOwnCrew(crew, config?.crewId) : config?.accountType !== 'company' && /\b(captain|master)\b/i.test(getVal(crew['Position']) || ''))
    && (!can || (can('ai', 'read') && can('rotations', 'read')))
  const [tab, setTab] = useState(() => { const t = localStorage.getItem('hc_open_crew_tab'); if (t) { localStorage.removeItem('hc_open_crew_tab'); return t } return 'info' })
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [showLogModal, setShowLogModal] = useState(false)
  const [docs, setDocs] = useState([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [extracting, setExtracting] = useState(false)
  const [extractedData, setExtractedData] = useState(null)
  const [leave, setLeave] = useState([])
  const [leaveLoading, setLeaveLoading] = useState(false)
  const [showLeaveForm, setShowLeaveForm] = useState(false)
  const [leaveForm, setLeaveForm] = useState({ 'Leave Type': 'Leave', 'Start Date': '', 'End Date': '', 'Notes': '' })
  const [editRot, setEditRot] = useState(false)
  const name = `${getVal(crew['First Name'])} ${getVal(crew['Last Name'])}`
  const dept = getVal(crew['Department'])
  const deptStyle = DEPT_COLORS[dept] || { bg: 'rgba(255,255,255,.06)', tc: 'var(--muted)' }
  const ob = isOnboard(crew)
  const todayISO = new Date().toISOString().split('T')[0]
  const rot = rotationPeriods(leave, { onboard: ob, todayISO })

  useEffect(() => {
    if (tab === 'log') loadLogs()
    if (tab === 'docs') loadDocs()
    if (tab === 'leave' || tab === 'rotation') loadLeave()
  }, [tab, crew['Onboard']])

  function leaveDays(a, b) {
    if (!a || !b) return 0
    const d1 = new Date(a + 'T12:00:00'), d2 = new Date(b + 'T12:00:00')
    if (isNaN(d1) || isNaN(d2)) return 0
    return Math.max(0, Math.round((d2 - d1) / 86400000) + 1)
  }

  async function loadLeave() {
    setLeaveLoading(true)
    try {
      const data = await fetchRows('crew_leave')
      const firstName = getVal(crew['First Name']).toLowerCase()
      const lastName = getVal(crew['Last Name']).toLowerCase()
      const crewId = getVal(crew['Crew ID'])
      setLeave((data || []).filter(l => {
        const lname = getVal(l['Crew Name']).toLowerCase()
        const lid = getVal(l['Crew ID'])
        return (crewId && lid === crewId) || (lname.includes(firstName) && lname.includes(lastName))
      }))
    } catch(e) { setLeave([]) }
    finally { setLeaveLoading(false) }
  }

  async function addLeave() {
    const isPaidOff = leaveForm['Leave Type'] === 'Paid Off'
    const sd = leaveForm['Start Date'], ed = leaveForm['End Date']
    if (isPaidOff) {
      const d = Number(leaveForm['Days'])
      if (!d || d <= 0) { alert('Enter the number of days paid off.'); return }
    } else if (!sd) { alert('Please set the start date.'); return }
    else if (ed && ed < sd) { alert('The end date is before the start date.'); return }
    try {
      await createRow('crew_leave', {
        'Crew ID': getVal(crew['Crew ID']) || '',
        'Crew Name': name,
        'Leave Type': leaveForm['Leave Type'] || 'Leave',
        'Start Date': isPaidOff ? new Date().toISOString().slice(0,10) : sd,
        'End Date': isPaidOff ? new Date().toISOString().slice(0,10) : (ed || null),
        'Days': isPaidOff ? Number(leaveForm['Days']) : (ed ? leaveDays(sd, ed) : 0),
        'Notes': leaveForm['Notes'] || '',
      })
      setShowLeaveForm(false)
      setLeaveForm({ 'Leave Type': 'Leave', 'Start Date': '', 'End Date': '', 'Notes': '' })
      loadLeave()
    } catch(e) { alert('Could not add leave: ' + e.message + (!ed && !isPaidOff ? ' (If the database requires an end date, run sql/rotation-open-ended.sql first.)' : '')) }
  }

  async function deleteLeave(id) {
    if (!window.confirm('Delete this leave period? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'crew_leave', id, label: 'leave period', onUndone: () => loadLeave() }); setLeave(prev => prev.filter(l => l.id !== id)) } catch(e) { alert('Could not delete: ' + (e.message || e)) }
  }

  async function loadLogs() {
    setLogsLoading(true)
    try {
      const data = await fetchRows('crew_log')
      const firstName = getVal(crew['First Name']).toLowerCase()
      const lastName = getVal(crew['Last Name']).toLowerCase()
      const crewId = getVal(crew['Crew ID'])
      setLogs(data.filter(l => {
        const lm = getVal(l['Crew Member']).toLowerCase()
        const lid = getVal(l['Crew ID'])
        return lid === crewId || lm.includes(firstName) || lm.includes(lastName)
      }))
    } catch(e) { console.error(e) }
    finally { setLogsLoading(false) }
  }

  async function loadDocs() {
    setDocsLoading(true)
    try {
      const data = await fetchRows('crew_docs')
      const crewId = getVal(crew['Crew ID'])
      const firstName = getVal(crew['First Name']).toLowerCase()
      const lastName = getVal(crew['Last Name']).toLowerCase()
      setDocs(data.filter(d => {
        if (d['Crew Record']) return String(d['Crew Record']) === String(crew.id)
        const title = (d['Title'] || '').toLowerCase()
        return title.includes(firstName) || title.includes(lastName)
      }))
    } catch(e) { console.error(e) }
    finally { setDocsLoading(false) }
  }

  async function loadDocs() {
    setDocsLoading(true)
    try {
      const data = await fetchRows('crew_docs')
      const crewId = getVal(crew['Crew ID'])
      const firstName = getVal(crew['First Name']).toLowerCase()
      const lastName = getVal(crew['Last Name']).toLowerCase()
      setDocs(data.filter(d => {
        if (d['Crew Record']) return String(d['Crew Record']) === String(crew.id)
        const title = (d['Title'] || '').toLowerCase()
        return title.includes(firstName) || title.includes(lastName)
      }))
    } catch(e) { console.error(e) }
    finally { setDocsLoading(false) }
  }

  // the Crew Log entry does exactly what the on-board toggle does (src/lib/signEvent.js): flag, log line and rotation
  async function handleSaveLog(form) {
    const r = await runSignEvent(crew, {
      signingOn: (form['Action'] || 'Signed On') === 'Signed On',
      date: form['Date From'] || new Date().toISOString().split('T')[0],
      dateTo: form['Date To'] || '', port: form['Port'] || '', status: form['Employment Status'] || '',
      notes: form['Notes'] || '', entry: form['Log Entry'] || '',
    }, row => { if (onCrewChanged) onCrewChanged(row) })
    loadLogs(); loadLeave()
    if (r.warn.length) alert(r.warn.join('\n'))
  }

  async function handleUploadCert(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 18 * 1024 * 1024) {
      alert('File too large. Please upload a PDF under 18MB.')
      return
    }
    setExtracting(true)
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader()
        r.onload = () => res(r.result.split(',')[1])
        r.onerror = () => rej(new Error('Read failed'))
        r.readAsDataURL(file)
      })
      const { data: { session } } = await (await import('../lib/supabase.js')).supabase.auth.getSession()
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }
      const uploadResp = await fetch('/api/upload-cert', {
        method: 'POST',
        headers,
        body: JSON.stringify({ base64, filename: file.name, area: 'crew_certs', client_id: config?.id }),
      })
      const { url: fileUrl } = await uploadResp.json()
      const extractResp = await fetch('/api/extract-cert', {
        method: 'POST',
        headers,
        body: JSON.stringify({ base64, crewName: name }),
      })
      const extracted = await extractResp.json()
      setEditingDoc(null)
      setShowDocModal(true)
      setExtractedData({ ...extracted, 'File URL': fileUrl || '' })
    } catch(e) {
      console.error(e)
      alert('Failed to extract certificate data')
    } finally {
      setExtracting(false)
    }
  }

  function toBaserowDate(val) {
    if (!val) return ''
    const s = String(val).trim()
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    // Handle MM / DD / YYYY with spaces (date picker format)
    const spaced = s.replace(/\s*\/\s*/g, '/')
    const parts = spaced.split('/')
    if (parts.length === 3) {
      const [a, b, c] = parts.map(p => p.trim().padStart(2, '0'))
      // If first part is 4 digits it's YYYY/MM/DD
      if (a.length === 4) return `${a}-${b}-${c}`
      // Otherwise MM/DD/YYYY
      if (c.length === 4) return `${c}-${a}-${b}`
    }
    // Try parsing as fallback
    const d = new Date(s)
    if (isNaN(d)) return ''
    return d.toISOString().split('T')[0]
  }

  async function handleDeleteDoc(doc) {
    const label = 'document "' + (doc['Title'] || 'document') + '"'
    if (!window.confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try {
      await deleteWithUndo(supabase, { table: 'crew_docs', id: doc.id, label, onUndone: () => loadDocs() })
      setDocs(prev => prev.filter(d => d.id !== doc.id))
    } catch(e) { console.error(e) }
  }

  async function handleSaveDoc(form, replaceId) {
    const issuedDate = toBaserowDate(form['Issued Date'])
    const expiryDate = toBaserowDate(form['Expiry Date'])
    const status = storedStatus(expiryDate, expiryToday(), { soon: 'Expiring Soon', none: 'Valid' })   // the shared expiry rule (src/lib/expiry.js)
    const titleVal = (form['Title'] || '').trim()
    const finalTitle = titleVal && !titleVal.toLowerCase().includes(name.toLowerCase().split(' ')[0].toLowerCase())
      ? `${name} - ${titleVal}`
      : titleVal
    const body = {
      'Title': finalTitle,
      'Crew Record': crew.id,   // their own documents are found by it (sql/test-fixes-5.sql)
      'Type': form['Type'] || '',
      'Doc ID': form['Doc ID'] || '',
      'File URL': form['File URL'] || '',
      'Notes': form['Notes'] || '',
      'Status': status,
    }
    if (issuedDate) body['Issued Date'] = issuedDate
    if (expiryDate) body['Expiry Date'] = expiryDate
    const target = editingDoc || (replaceId ? docs.find(d => d.id === replaceId) : null)
    if (target) {
      await updateRow('crew_docs', target.id, body)
      setDocs(prev => prev.map(d => d.id === target.id ? { ...d, ...body } : d))
    } else {
      const newDoc = await createRow('crew_docs', body)
      setDocs(prev => [newDoc, ...prev])
    }
    setShowDocModal(false)
    setEditingDoc(null)
  }

  const sections = [
    { title: 'Personal', fields: ['Date of Birth','Place of Birth','Nationality'] },
    { title: 'Contact', fields: ['Phone','Email','Emergency Contact Name','Emergency Contact Phone'] },
    { title: 'Contract', fields: ['Contract Start Date','Contract End Date','Status'] },
    { title: 'Passport', fields: ['Passport No','Passport Issue Place','Passport Issued Date','Passport Expiry Date'] },
    { title: "Seaman's Book", fields: ["Seaman's Book No","Seaman's Book Expiry"] },
    { title: 'Notes', fields: ['Notes'] },
  ]

  return (
    <>
      <div style={cs.overlay} onClick={onClose}>
        <div style={cs.panel} onClick={e => e.stopPropagation()}>
          <div style={cs.panelHeader}>
            <Avatar name={name} photo={getVal(crew['Photo URL'])} size={56} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{getVal(crew['Position'])}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {dept && <span style={{ ...cs.deptBadge, background: deptStyle.bg, color: deptStyle.tc }}>{dept}</span>}
                <OnboardSlider value={ob} onChange={() => onToggleOnboard(crew, ob)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {ownForHandover && <button className="act-btn" title="The assistant writes your handover from the records since your last sign-on (one card to approve)" onClick={() => { onClose(); askWriteHandover() }}>Write my handover</button>}
              <button className="act-btn" onClick={() => onEdit(crew)}>Edit</button>
              <button style={cs.closeBtn} onClick={onClose}>✕</button>
            </div>
          </div>

          <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)', background: 'var(--hull)', flexShrink: 0 }}>
            <button style={{ ...cs.tabBtn, ...(tab === 'info' ? cs.tabActive : {}) }} onClick={() => setTab('info')}>Profile</button>
            <button style={{ ...cs.tabBtn, ...(tab === 'log' ? cs.tabActive : {}) }} onClick={() => setTab('log')}>Log History</button>
            <button style={{ ...cs.tabBtn, ...(tab === 'docs' ? cs.tabActive : {}) }} onClick={() => setTab('docs')}>Crew Docs</button>
            <button style={{ ...cs.tabBtn, ...(tab === 'leave' ? cs.tabActive : {}) }} onClick={() => setTab('leave')}>Leave</button>
            <button style={{ ...cs.tabBtn, ...(tab === 'rotation' ? cs.tabActive : {}) }} onClick={() => setTab('rotation')}>Rotation</button>
          </div>

          {tab === 'info' && (
            <div style={cs.panelBody}>
              {sections.map(sec => {
                const hasData = sec.fields.some(f => getVal(crew[f]))
                if (!hasData) return null
                return (
                  <div key={sec.title} style={cs.section}>
                    <div style={cs.sectionTitle}>{sec.title}</div>
                    {sec.fields.map(f => {
                      const val = getVal(crew[f])
                      if (!val) return null
                      const isExpiryDate = f.includes('Expiry') || f === 'Contract End Date' || f === "Seaman's Book Expiry"
                      return (
                        <div key={f} style={cs.detailRow}>
                          <span style={cs.detailLabel}>{f}</span>
                          <span style={{ ...cs.detailVal, color: isExpiryDate ? dateColor(val) : 'var(--text)' }}>
                            {val}{isExpiryDate && isExpired(val) ? ' ⚠ Expired' : isExpiryDate && isExpiringSoon(val) ? ' ⚠ Soon' : ''}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}

          {tab === 'log' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setShowLogModal(true)}>+ Add Entry</button>
              </div>
              {logsLoading && <div className="loading-row"><div className="spin" />Loading...</div>}
              {!logsLoading && logs.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No log entries found.</div>}
              {!logsLoading && logs.map((log, i) => {
                const action = getVal(log['Action'])
                const isSignedOn = action === 'Signed On'
                return (
                  <div key={i} style={{ borderBottom: '0.5px solid var(--border)', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: isSignedOn ? '#137050' : '#b4313c' }}>
                        {isSignedOn ? '● ' : '○ '}{action}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>{getVal(log['Date From'])}</span>
                    </div>
                    {getVal(log['Log Entry']) && <div style={{ fontSize: 12, color: 'var(--text)' }}>{getVal(log['Log Entry'])}</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {getVal(log['Employment Status']) && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'var(--chip)', color: 'var(--muted)' }}>{getVal(log['Employment Status'])}</span>}
                      {getVal(log['Port']) && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'var(--chip)', color: 'var(--muted)' }}>📍 {getVal(log['Port'])}</span>}
                    </div>
                    {getVal(log['Notes']) && <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>{getVal(log['Notes'])}</div>}
                  </div>
                )
              })}
            </div>
          )}

          {tab === 'docs' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleUploadCert} />
                  <span className="btn btn-primary" style={{ fontSize: 11, padding: '6px 12px' }}>
                    {extracting ? 'Reading PDF...' : 'Upload Certificate'}
                  </span>
                </label>
                <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => { setEditingDoc(null); setExtractedData(null); setShowDocModal(true) }}>+ Add Document</button>
              </div>
              {docsLoading && <div className="loading-row"><div className="spin" />Loading...</div>}
              {!docsLoading && docs.length === 0 && (
                <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No documents found for this crew member.</div>
              )}
              {!docsLoading && docs.map((doc, i) => {
                const title = doc['Title'] || ''
                const type = getVal(doc['Type'])
                const issuedDate = doc['Issued Date'] || ''
                const expiryDate = doc['Expiry Date'] || ''
                const fileUrl = doc['File URL'] || ''
                const expired = expiryDate && isExpired(expiryDate)
                const expiringSoon = expiryDate && !expired && isExpiringSoon(expiryDate)
                const statusColor = expired ? '#b4313c' : expiringSoon ? '#e8b96a' : '#137050'
                const statusLabel = expired ? '⚠ Expired' : expiringSoon ? '⚠ Expiring Soon' : '✓ Valid'
                return (
                  <div key={i} onClick={() => { setEditingDoc(doc); setShowDocModal(true) }} style={{ borderBottom: '0.5px solid var(--border)', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {type && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'var(--chip)', color: 'var(--muted)', alignSelf: 'flex-start' }}>{type}</span>}
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title || '—'}</span>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: statusColor, flexShrink: 0, marginLeft: 8 }}>{statusLabel}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      {issuedDate && (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          Issued: <span style={{ color: 'var(--text)' }}>{issuedDate}</span>
                        </div>
                      )}
                      {expiryDate && (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          Expires: <span style={{ color: statusColor }}>{expiryDate}</span>
                        </div>
                      )}
                    </div>
                    {fileUrl && (
                      <a href={fileHref(fileUrl)} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--gold2)', textDecoration: 'none' }}>
                        📎 View Document
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {tab === 'leave' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setShowLeaveForm(v => !v)}>{showLeaveForm ? 'Close' : '+ Add Leave'}</button>
              </div>
              {showLeaveForm && (
                <div style={{ background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Type</label>
                    <select value={leaveForm['Leave Type']} onChange={e => setLeaveForm(f => ({ ...f, 'Leave Type': e.target.value }))} style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit' }}>
                      {['Leave','Paid Off','Working','Working ashore','Unpaid leave','Training','Unpaid','Medical'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {leaveForm['Leave Type'] === 'Paid Off' ? (
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Days paid off</label>
                      <input type="number" min="0" value={leaveForm['Days'] || ''} onChange={e => setLeaveForm(f => ({ ...f, 'Days': e.target.value }))} placeholder="e.g. 10" style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    </div>
                  ) : (<>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Start</label><DateField value={leaveForm['Start Date']} onChange={v => setLeaveForm(f => ({ ...f, 'Start Date': v || '' }))} /></div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>End (empty = ongoing)</label><DateField value={leaveForm['End Date']} onChange={v => setLeaveForm(f => ({ ...f, 'End Date': v || '' }))} /></div>
                  </div>
                  {leaveForm['Start Date'] && !leaveForm['End Date'] && leaveForm['Leave Type'] !== 'Paid Off' && (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>No end date: ongoing. It counts the days up to today and closes by itself the day before the person signs on.</div>
                  )}
                  {leaveForm['Start Date'] && leaveForm['End Date'] && (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Duration: <b style={{ color: 'var(--text)' }}>{leaveDays(leaveForm['Start Date'], leaveForm['End Date'])} days</b></div>
                  )}
                  </>)}
                  <div><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Notes</label><input value={leaveForm['Notes']} onChange={e => setLeaveForm(f => ({ ...f, 'Notes': e.target.value }))} style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} /></div>
                  <button className="btn btn-primary" style={{ fontSize: 12, alignSelf: 'flex-end' }} onClick={addLeave}>Save Leave</button>
                </div>
              )}
              {leaveLoading && <div className="loading-row"><div className="spin" />Loading...</div>}
              {!leaveLoading && leave.length === 0 && (
                <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No leave recorded for this crew member.</div>
              )}
              {!leaveLoading && (() => {
                const baseEnt = Number(getVal(crew['Leave Entitlement Days'])) || 0
                const contractStart = getVal(crew['Contract Start Date']) || ''
                const csYear = contractStart ? Number(String(contractStart).slice(0, 4)) : null
                // Excel-style accrual: (entitlement / 365) x total days under contract (start -> today)
                let accrued = 0, daysUnderContract = 0
                if (contractStart) {
                  const cs = new Date(contractStart + 'T12:00:00')
                  const now = new Date()
                  daysUnderContract = Math.max(0, Math.round((now - cs) / 86400000) + 1)
                  accrued = (baseEnt / 365) * daysUnderContract
                }
                // same periods and days as the Rotation tab (src/lib/rotationPeriods.js): dates decide, ongoing counts to today
                const leaveOnly = rot.periods.filter(p => p.type === 'Leave')
                const byYear = {}
                ;(leaveOnly).forEach(l => {
                  const y = String(l.start || '').slice(0, 4) || '—'
                  ;(byYear[y] = byYear[y] || []).push(l)
                })
                // Compute oldest -> newest so carry-over accumulates, then display newest first.
                const yearsAsc = Object.keys(byYear).filter(y => y !== '—').sort()
                const calc = {}
                let carry = 0
                yearsAsc.forEach(y => {
                  const yr = Number(y)
                  let ent = baseEnt
                  // Pro-rate the joining year by days aboard from contract start to Dec 31.
                  if (csYear && yr === csYear && contractStart) {
                    const start = new Date(contractStart + 'T12:00:00')
                    const yearEnd = new Date(yr, 11, 31, 12)
                    const daysAboard = Math.max(0, Math.round((yearEnd - start) / 86400000) + 1)
                    ent = Math.round(baseEnt * (daysAboard / 365) * 100) / 100
                  } else if (csYear && yr < csYear) {
                    ent = 0
                  }
                  const items = byYear[y]
                  const taken = items.reduce((sum, l) => sum + l.days, 0)
                  const available = Math.round((ent + carry) * 100) / 100
                  const remaining = Math.round((available - taken) * 100) / 100
                  calc[y] = { ent, carry, taken, remaining, available }
                  carry = remaining  // full remainder rolls to next year
                })
                const years = yearsAsc.slice().reverse()
                const totalTaken = rot.leave
                const totalPaidOff = (leave || []).filter(l => (l['Leave Type'] || '') === 'Paid Off').reduce((sum, l) => sum + (Number(l['Days']) || leaveDays(l['Start Date'], l['End Date'])), 0)
                const balance = accrued - totalTaken - totalPaidOff
                const summaryCard = (
                  <div key="__accrual" style={{ marginBottom: 22, padding: '14px 16px', background: 'var(--card, #11161e)', border: '0.5px solid var(--border)', borderRadius: 12 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Accrued Leave (contract to date)</div>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13, color: 'var(--text)' }}>
                      <span>Accrued <b style={{ color: 'var(--gold2)' }}>{accrued.toFixed(2)}</b></span>
                      <span>Taken <b style={{ color: '#e8b96a' }}>{totalTaken}</b></span>
                      {totalPaidOff > 0 && <span>Paid Off <b style={{ color: '#e8b96a' }}>{totalPaidOff}</b></span>}
                      <span>Balance <b style={{ color: balance < 0 ? '#b4313c' : '#137050' }}>{balance.toFixed(2)}</b></span>
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 6 }}>{baseEnt}/yr &times; {daysUnderContract} days under contract &divide; 365</div>
                  </div>
                )
                return [summaryCard, ...years.map(y => {
                  const items = byYear[y]
                  const c = calc[y] || { ent: baseEnt, carry: 0, taken: 0, remaining: baseEnt }
                  const entitlement = c.ent, taken = c.taken, remaining = c.remaining, carried = c.carry
                  return (
                    <div key={y} style={{ marginBottom: 22 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{y}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          Entitlement <b style={{ color: 'var(--text)' }}>{entitlement}</b>{carried ? <> + Carried <b style={{ color: 'var(--text)' }}>{carried}</b></> : null} · Taken <b style={{ color: '#e8b96a' }}>{taken}</b> · Remaining <b style={{ color: remaining < 0 ? '#b4313c' : '#137050' }}>{remaining}</b>
                        </div>
                      </div>
                      {items.map((l, i) => (
                        <div key={(l.ids && l.ids[0]) || i} style={{ borderBottom: '0.5px solid var(--border)', padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'var(--chip)', color: 'var(--muted)', alignSelf: 'flex-start' }}>{l.type}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{l.start} → {l.noEnd ? 'no end date' : l.ongoing ? 'ongoing' : l.end}</span>
                            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{l.days} days{l.ongoing ? ' so far' : ''}{l.notes ? ' · ' + l.notes : ''}</span>
                          </div>
                          {l.ids.length === 1
                            ? <button onClick={() => deleteLeave(l.ids[0])} style={{ background: 'none', border: 'none', color: '#b4313c', fontSize: 16, cursor: 'pointer' }}>×</button>
                            : <span title="Joined from several records: change them under Rotation › Edit periods" style={{ fontSize: 10, color: 'var(--muted)' }}>{l.ids.length} records</span>}
                        </div>
                      ))}
                    </div>
                  )
                })]
              })()}
            </div>
          )}

          {tab === 'rotation' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {leaveLoading && <div className="loading-row"><div className="spin" />Loading...</div>}
              {!leaveLoading && (leave || []).length === 0 && (
                <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No rotation data for this crew member.</div>
              )}
              {!leaveLoading && (() => {
                const fmt = d => { if (!d) return ''; const m = new Date(d + 'T12:00:00'); return isNaN(m) ? d : m.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }
                // a period with an end date ends there; "ongoing" only without an end date while signed on (src/lib/rotationPeriods.js)
                const totalWork = rot.worked, totalLeave = rot.leave
                const rotations = rot.periods.slice().reverse()
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '0.5px solid var(--border)' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Rotation history</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Worked <b style={{ color: '#137050' }}>{totalWork}</b> · Leave <b style={{ color: '#b4313c' }}>{totalLeave}</b>{rot.other > 0 && <> · Other <b style={{ color: 'var(--text)' }}>{rot.other}</b></>}</div>
                        <button onClick={() => setEditRot(v => !v)} style={{ background: 'none', border: '0.5px solid var(--border)', color: 'var(--gold2)', fontSize: 11, cursor: 'pointer', padding: '4px 10px', borderRadius: 6 }}>{editRot ? 'Done' : 'Edit periods'}</button>
                      </div>
                    </div>
                    {editRot && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Editing raw periods (each row is one record). Change type or dates, then Done.</div>
                        {[...(leave || [])].sort((a, b) => String(b['Start Date']).localeCompare(String(a['Start Date']))).map((l) => (
                          <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}>
                            <select value={l['Leave Type'] || 'Leave'} onChange={async e => { await updateRow('crew_leave', l.id, { 'Leave Type': e.target.value }); loadLeave() }} style={{ background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 11, padding: '5px 7px', borderRadius: 5, fontFamily: 'inherit' }}>
                              {['Working','Working ashore','Leave','Unpaid leave','Training','Unpaid','Medical'].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <div style={{ width: 132 }}><DateField value={l['Start Date'] || ''} onChange={async v => { await updateRow('crew_leave', l.id, { 'Start Date': v || '', 'Days': leaveDays(v, l['End Date']) }); loadLeave() }} /></div>
                            <span style={{ color: 'var(--muted)', fontSize: 11 }}>→</span>
                            <div style={{ width: 132 }}><DateField value={l['End Date'] || ''} onChange={async v => { await updateRow('crew_leave', l.id, { 'End Date': v || '', 'Days': leaveDays(l['Start Date'], v) }); loadLeave() }} /></div>
                            <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 32 }}>{Number(l['Days']) || leaveDays(l['Start Date'], l['End Date'])}d</span>
                            <button onClick={() => deleteLeave(l.id)} style={{ background: 'none', border: 'none', color: '#b4313c', fontSize: 15, cursor: 'pointer', marginLeft: 'auto' }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {rotations.map((m, i) => {
                        const isWork = m.type === 'Working' || m.type === 'Working ashore'
                        const days = m.days
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 84, fontSize: 11, fontWeight: 700, color: isWork ? '#137050' : '#b4313c', flexShrink: 0 }}>{m.type}</div>
                            <div style={{ flex: 1, background: isWork ? 'rgba(93,186,142,.14)' : 'rgba(224,112,112,.14)', border: '0.5px solid ' + (isWork ? 'rgba(93,186,142,.4)' : 'rgba(224,112,112,.4)'), borderRadius: 6, padding: '8px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: 'var(--text)' }}>{fmt(m.start)} → {m.noEnd ? 'no end date' : m.ongoing ? 'today' : fmt(m.end)}{m.ongoing ? ' · ongoing' : ''}</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{m.noEnd ? '–' : days + 'd'}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      </div>
      {showDocModal && <DocModal doc={editingDoc} prefill={extractedData} crewName={name} existing={docs} onClose={() => { setShowDocModal(false); setEditingDoc(null); setExtractedData(null) }} onSave={handleSaveDoc} onDelete={(doc) => { handleDeleteDoc(doc); setShowDocModal(false); setEditingDoc(null) }} />}
      {showLogModal && <LogEntryModal name={name} onClose={() => setShowLogModal(false)} onSave={handleSaveLog} />}
    </>
  )
}

// Configure pdf.js worker

// Labelled field for the crew list toolbar (module level so inputs keep focus while typing).
function FalField({ t, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#67748c' }}>{t}</span>
      {children}
    </label>
  )
}

function FALModal({ rows, falFilter, setFalFilter, portDep, setPortDep, portArr, setPortArr, dateDep, setDateDep, dateArr, setDateArr, config, onClose }) {
  const onboardCrew = rows.filter(r => isOnboard(r))
  const captain = onboardCrew.find(r => getVal(r['Position']).toLowerCase().includes('captain'))
  const chiefOfficer = onboardCrew.find(r => getVal(r['Position']).toLowerCase().includes('chief officer') || getVal(r['Position']).toLowerCase().includes('1st officer'))
  const defaultSignatory = captain ? `${getVal(captain['First Name'])} ${getVal(captain['Last Name'])}` : chiefOfficer ? `${getVal(chiefOfficer['First Name'])} ${getVal(chiefOfficer['Last Name'])}` : ''
  const captainName = defaultSignatory
  const signatoryOptions = onboardCrew.filter(r => { const pos = getVal(r['Position']).toLowerCase(); return pos.includes('captain') || pos.includes('1st officer') || pos.includes('chief officer') }).map(r => `${getVal(r['First Name'])} ${getVal(r['Last Name'])}`).filter(Boolean)
  const [signatory, setSignatory] = useState(defaultSignatory)
  const [excludedIds, setExcludedIds] = useState([])
  const [extraCrew, setExtraCrew] = useState([])
  const [showAddExtra, setShowAddExtra] = useState(false)
  const [savingArchive, setSavingArchive] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [archiveRows, setArchiveRows] = useState([])
  const [archiveLoading, setArchiveLoading] = useState(false)
  const [extraForm, setExtraForm] = useState({ position: '', firstName: '', lastName: '', nationality: '', dob: '', pob: '', passportNo: '', passportIssue: '', passportIssued: '', passportExpiry: '' })
  const [vp, setVp] = useState(null)
  const [movement, setMovement] = useState('') // '', 'arrival', 'departure'
  useEffect(() => {
    // particulars from Supabase (same row "Save particulars" writes), with the same labels: IMO, LOA, Flag...
    async function loadVP() {
      try {
        const rows = await fetchRows('vessel_particulars')
        if (rows && rows.length > 0) setVp(rows[0])
      } catch(e) {}
    }
    loadVP()
  }, [])
  const baseCrew = falFilter === 'onboard' ? rows.filter(r => isOnboard(r)) : rows
  // Rank order for an official crew list: senior first.
  const RANK_ORDER = ['captain','master','chief officer','1st officer','first officer','2nd officer','second officer','3rd officer','third officer','chief engineer','2nd engineer','second engineer','3rd engineer','third engineer','bosun','chief stewardess','chief steward','purser','head chef','chef','cook','2nd steward','second steward','3rd stew','third stew','4th stew','stewardess','steward','lead deckhand','deckhand','deck','engineer','interior']
  const rankIndex = r => {
    const pos = getVal(r['Position']).toLowerCase()
    for (let i = 0; i < RANK_ORDER.length; i++) { if (pos.includes(RANK_ORDER[i])) return i }
    return RANK_ORDER.length + 1
  }
  const falCrew = [...baseCrew.filter(r => !excludedIds.includes(r.id)), ...extraCrew]
    .sort((a, b) => rankIndex(a) - rankIndex(b))
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  const vesselRows = [
    [['Vessel Name', config?.vessel_name || ''], ['IMO #', vp?.['IMO'] || ''], ['LOA', vp?.['LOA'] || '']],
    [['Nationality (flag)', vp?.['Flag'] || ''], ['MMSI #', vp?.['MMSI'] || ''], ['Beam overall', vp?.['Beam'] || '']],
    [['Registration #', vp?.['Registration'] || ''], ['Call Sign', vp?.['Call Sign'] || ''], ['Draft', vp?.['Draft'] || '']],
    [['Home Port', vp?.['Home Port'] || ''], ['Port of departure', portDep], ['Gross tonnage', vp?.['Gross Tonnage'] || '']],
    [["Captain's name", captainName], ['Date of departure', fmtDocDate(dateDep)], ['Net tonnage', vp?.['Net Tonnage'] || '']],
    [['Email', vp?.['Email'] || ''], ['Port of arrival', portArr], ['Propulsion', vp?.['Propulsion'] || '']],
    [['Owner', vp?.['Owner'] || ''], ['Date of arrival', fmtDocDate(dateArr)], ['', '']],
  ]

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 0' }}>
      <div id="fal-print" style={{ background: '#fff', color: '#000', width: 1040, borderRadius: 8, padding: 20, fontFamily: 'Arial, sans-serif', colorScheme: 'light', boxSizing: 'border-box' }}>

        {/* Toolbar */}
        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => {
                const el = document.getElementById('fal-print')
                // Hide no-print elements
                const noPrint = el.querySelectorAll('.no-print')
                noPrint.forEach(el => el.style.display = 'none')
                const opt = {
                  margin: 8,
                  filename: `${new Date().toLocaleDateString('en-GB', {year:'numeric',month:'short',day:'2-digit'}).replace(/ /g,'-')}_${config?.vessel_name || 'vessel'}_Crew_List.pdf`,
                  image: { type: 'jpeg', quality: 0.98 },
                  html2canvas: { scale: 2, useCORS: true },
                  jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
                  pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }
                }
                html2pdf().set(opt).from(el).save().then(() => {
                  // Restore no-print elements
                  noPrint.forEach(el => el.style.display = '')
                })
              }} style={{ padding: '6px 16px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Export to PDF</button>
            <button onClick={() => window.print()} style={{ padding: '6px 16px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Print</button>
            <button disabled={savingArchive} onClick={async () => {
              setSavingArchive(true)
              try {
                const el = document.getElementById('fal-print')
                const noPrint = el.querySelectorAll('.no-print')
                noPrint.forEach(e => e.style.display = 'none')
                const opt = {
                  margin: 8,
                  filename: 'crew_list.pdf',
                  image: { type: 'jpeg', quality: 0.98 },
                  html2canvas: { scale: 2, useCORS: true },
                  jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
                  pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }
                }
                const blob = await html2pdf().set(opt).from(el).outputPdf('blob')
                noPrint.forEach(e => e.style.display = '')
                const dateStr = new Date().toISOString().split('T')[0]
                const path = filePath('crew_lists', config?.id, dateStr + '_crew_list.pdf')
                const { error: uploadError } = await supabase.storage.from('crew-docs').upload(path, blob, { contentType: 'application/pdf', upsert: false })
                if (uploadError) throw new Error(uploadError.message)
                await createRow('crew_list', {
                  'Title': `${config?.vessel_name || 'Vessel'} Crew List - ${dateStr}`,
                  'Date': dateStr,
                  'Signatory': signatory,
                  'Filter': falFilter === 'onboard' ? 'Onboard Crew' : 'All Crew',
                  'File URL': path,   // private bucket: the record keeps the PATH, opened through a signed link
                })
                alert('Crew list saved to archive!')
              } catch(e) {
                alert('Failed to save: ' + e.message)
              } finally {
                setSavingArchive(false)
              }
            }} style={{ padding: '6px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
              {savingArchive ? 'Saving...' : 'Save to Archive'}
            </button>
            <button onClick={async () => {
              setShowArchive(p => !p)
              if (!showArchive) {
                setArchiveLoading(true)
                try {
                  const data = await fetchRows('crew_list')
                  setArchiveRows(data || [])
                } catch(e) { setArchiveRows([]) }
                finally { setArchiveLoading(false) }
              }
            }} style={{ padding: '6px 16px', background: showArchive ? 'var(--gold)' : '#f3f4f6', color: showArchive ? '#000' : '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Archive</button>
            <button onClick={onClose} style={{ padding: '6px 16px', background: '#e5e7eb', color: '#000', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Close</button>
          </div>
        </div>

          <div className="no-print" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16, padding: '12px 14px', background: '#f8f9fb', border: '1px solid #e8ebf0', borderRadius: 10 }}>
            {(() => {
              const L = FalField
              const fld = { height: 34, boxSizing: 'border-box', padding: '0 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, color: '#0f2240', background: '#fff' }
              const seg = on => ({ height: 34, padding: '0 14px', border: 'none', background: on ? '#0f2240' : 'transparent', color: on ? '#fff' : '#51607a', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' })
              return (
                <>
                  <L t="Movement">
                    <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <button type="button" style={seg(movement === 'arrival')} onClick={() => setMovement(v => v === 'arrival' ? '' : 'arrival')}>Arrival</button>
                      <button type="button" style={{ ...seg(movement === 'departure'), borderLeft: '1px solid #d1d5db' }} onClick={() => setMovement(v => v === 'departure' ? '' : 'departure')}>Departure</button>
                    </div>
                  </L>
                  <L t="Crew">
                    <select value={falFilter} onChange={e => setFalFilter(e.target.value)} style={{ ...fld, width: 140 }}>
                      <option value="onboard">Onboard crew</option>
                      <option value="all">All crew</option>
                    </select>
                  </L>
                  <L t="Port of departure"><input placeholder="e.g. Antibes" value={portDep} onChange={e => setPortDep(e.target.value)} style={{ ...fld, width: 150 }} /></L>
                  <L t="Departure date"><DatePicker value={dateDep} onChange={setDateDep} width={150} /></L>
                  <L t="Port of arrival"><input placeholder="e.g. Genoa" value={portArr} onChange={e => setPortArr(e.target.value)} style={{ ...fld, width: 150 }} /></L>
                  <L t="Arrival date"><DatePicker value={dateArr} onChange={setDateArr} width={150} min={dateDep || undefined} /></L>
                  <L t="Signed by">
                    <select value={signatory} onChange={e => setSignatory(e.target.value)} style={{ ...fld, width: 170 }}>
                      {signatoryOptions.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </L>
                </>
              )
            })()}
          </div>
        {/* Archive panel */}
        {showArchive && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 12 }}>Crew List Archive</div>
            {archiveLoading && <div style={{ color: '#6b7280', fontSize: 12 }}>Loading...</div>}
            {!archiveLoading && archiveRows.length === 0 && <div style={{ color: '#6b7280', fontSize: 12 }}>No archived crew lists yet.</div>}
            {!archiveLoading && archiveRows.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #e5e7eb', fontSize: 12 }}>
                <div>
                  <div style={{ fontWeight: 500, color: '#111' }}>{r['Title'] || '-'}</div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>{r['Date']} · {r['Signatory']} · {r['Filter']}</div>
                </div>
                {r['File URL'] && <a href={fileHref(r['File URL'])} target="_blank" rel="noreferrer" style={{ color: '#1a56db', fontSize: 12 }}>📎 View PDF</a>}
              </div>
            ))}
          </div>
        )}

        {/* Title */}
        {/* HEYCAPTAIN letterhead */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '3px solid #C9A84C', paddingBottom: 9, marginBottom: 4 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <span style={{ display: 'inline-block', fontSize: 34, fontWeight: 900, color: '#1155F5', lineHeight: 1, letterSpacing: -3 }}>{'\u2248'}</span>
              <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.13em', color: '#0A1628' }}>HEYCAPTAIN</span>
            </div>
            <div style={{ fontSize: 9.5, color: '#67748C', letterSpacing: '.22em', textTransform: 'uppercase', marginTop: 4, marginLeft: 47 }}>Crew List</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 9.5, color: '#67748C', lineHeight: 1.7 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#16233B', letterSpacing: '.06em' }}>{(config?.vessel_name || 'VESSEL').toUpperCase()}</div>
            <div>{today}</div>
          </div>
        </div>
        <div style={{ background: '#0A1628', color: '#fff', padding: '6px 11px', fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', margin: '0 0 16px' }}>
          {falFilter === 'onboard' ? 'Crew Onboard' : 'All Crew'} &middot; {falCrew.length} listed{movement ? ' \u00b7 ' + movement : ''}
        </div>

        {/* Vessel info */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 11, border: '0.5px solid #555' }}>
          <tbody>
            {vesselRows.map((row, i) => (
              <tr key={i}>
                {row.map(([label, value], j) => (
                  <>
                    <td key={j+"l"} style={{ border: '1px solid #E5E9F1', padding: '5px 7px', fontWeight: 700, background: '#f3f4f6', whiteSpace: 'nowrap', width: '12%', color: '#000' }}>{label}</td>
                    <td key={j+"v"} style={{ border: '1px solid #E5E9F1', padding: '5px 7px', width: '21%', color: '#000' }}>{value}</td>
                  </>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Crew table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 20, border: '0.5px solid #555', tableLayout: 'fixed', wordWrap: 'break-word' }}>
          <thead>
            <tr style={{ background: '#e5e7eb', color: '#000' }}>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'center', width: 40 }}>#</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Position</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>First Name</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Last Name</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Nationality</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Date of Birth</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Place of Birth</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Passport No.</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Place of Issue</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Issued Date</th>
              <th style={{ border: '1px solid #0A1628', padding: '6px 7px', background: '#0A1628', color: '#fff', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700, textAlign: 'left' }}>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {falCrew.map((r, i) => (
              <Fragment key={r.id || i}>
              {i > 0 && i % 11 === 0 && (<tr className="html2pdf__page-break" style={{ height: 0 }}><td colSpan={10} style={{ padding: 0, border: 'none' }}></td></tr>)}
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', textAlign: 'center', color: '#000' }}>
                  <span className="no-print" onClick={() => r.id ? setExcludedIds(p => [...p, r.id]) : setExtraCrew(p => p.filter((ec, ei) => ei !== i - baseCrew.filter(x => !excludedIds.includes(x.id)).length))} style={{ cursor: 'pointer', color: '#b4313c', marginRight: 4, fontWeight: 700, fontSize: 14 }}>×</span>
                  {getVal(r['Crew ID']) || (i + 1)}
                </td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Position'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['First Name'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Last Name'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Nationality'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{fmtDocDate(getVal(r['Date of Birth']))}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Place of Birth'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Passport No'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{getVal(r['Passport Issue Place'])}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{fmtDocDate(getVal(r['Passport Issued Date']))}</td>
                <td style={{ border: '1px solid #E5E9F1', padding: '5px 7px', color: '#000' }}>{fmtDocDate(getVal(r['Passport Expiry Date']))}</td>
              </tr>
              </Fragment>
            ))}
          </tbody>
        </table>

        {/* Add extra crew */}
        <div className="no-print" style={{ marginBottom: 12 }}>
          {!showAddExtra
            ? <button onClick={() => setShowAddExtra(true)} style={{ padding: '5px 14px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>+ Add Crew Member</button>
            : <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', background: '#f9fafb', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <input placeholder="Position" value={extraForm.position} onChange={e => setExtraForm(p => ({...p, position: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 120 }} />
                <input placeholder="First Name" value={extraForm.firstName} onChange={e => setExtraForm(p => ({...p, firstName: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 100 }} />
                <input placeholder="Last Name" value={extraForm.lastName} onChange={e => setExtraForm(p => ({...p, lastName: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 100 }} />
                <input placeholder="Nationality" value={extraForm.nationality} onChange={e => setExtraForm(p => ({...p, nationality: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 100 }} />
                <input placeholder="DOB" value={extraForm.dob} onChange={e => setExtraForm(p => ({...p, dob: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 90 }} />
                <input placeholder="Passport No" value={extraForm.passportNo} onChange={e => setExtraForm(p => ({...p, passportNo: e.target.value}))} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 100 }} />
                <button onClick={() => {
                  setExtraCrew(p => [...p, { id: null, 'Position': extraForm.position, 'First Name': extraForm.firstName, 'Last Name': extraForm.lastName, 'Nationality': extraForm.nationality, 'Date of Birth': extraForm.dob, 'Place of Birth': extraForm.pob, 'Passport No': extraForm.passportNo, 'Passport Issue Place': extraForm.passportIssue, 'Passport Issued Date': extraForm.passportIssued, 'Passport Expiry Date': extraForm.passportExpiry, 'Crew ID': '' }])
                  setExtraForm({ position: '', firstName: '', lastName: '', nationality: '', dob: '', pob: '', passportNo: '', passportIssue: '', passportIssued: '', passportExpiry: '' })
                  setShowAddExtra(false)
                }} style={{ padding: '4px 12px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Add</button>
                <button onClick={() => setShowAddExtra(false)} style={{ padding: '4px 10px', background: '#e5e7eb', color: '#000', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              </div>
          }
        </div>

        {/* Footer */}
        <div style={{ fontSize: 11, marginBottom: 12 }}>
          <strong>Cash onboard less than Euro 10,000</strong>&nbsp;&nbsp;
          <label style={{ marginRight: 16 }}><input type="checkbox" /> YES</label>
          <label><input type="checkbox" /> NO</label>
        </div>
        <div style={{ fontSize: 11, marginBottom: 20, lineHeight: 1.6 }}>
          I, {(() => { const s = onboardCrew.find(r => `${getVal(r['First Name'])} ${getVal(r['Last Name'])}` === signatory); return s ? getVal(s['Position']) : 'Captain' })()} <strong>{signatory}</strong>, certify that this list of persons for the above named vessel is accurate and complete to the best of my knowledge and that I will report any change in crew to proper authorities as required by applicable regulations and laws of the nations concerned.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, marginBottom: 4 }}>Signed: {(() => { const s = onboardCrew.find(r => `${getVal(r['First Name'])} ${getVal(r['Last Name'])}` === signatory); return s ? getVal(s['Position']) : 'Captain' })()} <strong>{signatory}</strong></div>
            <div style={{ position: 'relative' }}>
              <canvas
                ref={el => { if (el && !el._initialized) {
                  el._initialized = true
                  el.width = 250; el.height = 80
                  const ctx = el.getContext('2d')
                  ctx.strokeStyle = '#000'; ctx.lineWidth = 1.5; ctx.lineCap = 'round'
                  let drawing = false
                  el.onmousedown = e => { drawing = true; ctx.beginPath(); ctx.moveTo(e.offsetX, e.offsetY) }
                  el.onmousemove = e => { if (!drawing) return; ctx.lineTo(e.offsetX, e.offsetY); ctx.stroke() }
                  el.onmouseup = () => { drawing = false }
                  el.onmouseleave = () => { drawing = false }
                  el.ontouchstart = e => { e.preventDefault(); drawing = true; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.beginPath(); ctx.moveTo(t.clientX-r.left, t.clientY-r.top) }
                  el.ontouchmove = e => { e.preventDefault(); if (!drawing) return; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.lineTo(t.clientX-r.left, t.clientY-r.top); ctx.stroke() }
                  el.ontouchend = () => { drawing = false }
                }}}
                id="sig-canvas"
                style={{ border: '0.5px solid #555', cursor: 'crosshair', display: 'block' }}
              />
              <button onClick={() => {
                const el = document.getElementById('sig-canvas')
                const ctx = el.getContext('2d')
                ctx.clearRect(0, 0, el.width, el.height)
              }} className="no-print" style={{ position:'absolute', top:2, right:2, background:'transparent', border:'none', cursor:'pointer', fontSize:10, color:'#5b6878' }}>Clear</button>
            </div>
          </div>
          <div style={{ fontSize: 11 }}>Date: <strong>{today}</strong></div>
        </div>
      </div>
    </div>
  )
}

export default function Crew({ onNavigate }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [deptFilter, setDeptFilter] = useState('')
  const [onboardFilter, setOnboardFilter] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [view, setView] = useState('cards')
  const { config: authConfig, isCrew } = useAuth()
  const [dataFor, setDataFor] = useState(null)   // { id, name }: the personal-data panel
  const [eraseDue, setEraseDue] = useState([])   // crew who left > 6 months ago, not erased yet (hc_crew_erase_due; Captain / management only)
  // the crew list (FAL) shows everyone's passport / birth data: only for logins that may see other crew members' personal data
  const seeOthers = canSeeOthers(authConfig?.accountType, authConfig?.permissions)
  const [accounts, setAccounts] = useState([])
  const [accLoading, setAccLoading] = useState(false)
  const [accModal, setAccModal] = useState(false)
  const [permPanel, setPermPanel] = useState(null) // crew account id
  const [permForm, setPermForm] = useState({})
  const [accForm, setAccForm] = useState({})
  const [accSaving, setAccSaving] = useState(false)
  const [accError, setAccError] = useState('')
  const [modal, setModal] = useState(null)
  // the edit form shows (and saves) the personal fields only where this login may see them: everyone's with the right,
  // a new crew member, or its own record (crew_id link) - never empty inputs over data it cannot read
  const formFields = (seeOthers || modal === 'new' || isOwnCrew(modal, authConfig?.crewId)) ? FORM_FIELDS : FORM_FIELDS.filter(f => !PERSONAL_FORM.includes(f.name))
  const [detailCrew, setDetailCrew] = useState(null)
  const [signAsk, setSignAsk] = useState(null)
  const [formData, setFormData] = useState({})
  // the form as it opened for an edit: a save sends only what changed (cleared fields as null)
  const [origForm, setOrigForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [showFAL, setShowFAL] = useState(false)
  const [falFilter, setFalFilter] = useState('onboard')
  const [portDep, setPortDep] = useState('')
  const [portArr, setPortArr] = useState('')
  const [dateDep, setDateDep] = useState('')
  const [dateArr, setDateArr] = useState('')
  const { config } = useAuth()

  // the reminder: only while the feature is on and for the Captain / management; the database checks the rights again
  async function loadEraseDue() {
    if (!canManageCrewData(authConfig)) return setEraseDue([])
    const { data, error } = await supabase.rpc('hc_crew_erase_due', { p_client: authConfig.id, p_months: ERASE_AFTER_MONTHS })
    setEraseDue(error ? [] : data || [])
  }
  useEffect(() => { loadEraseDue() }, [authConfig?.id])

  useEffect(() => {
    loadAndOpen()
    if (localStorage.getItem('hc_open_fal') === '1') {
      localStorage.removeItem('hc_open_fal')
      if (seeOthers) setShowFAL(true)
    }
  }, [])

  async function load() {
    setLoading(true); setError('')
    try { setRows(await fetchRows('crew')) }
    catch(e) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadAndOpen() {
    await new Promise(r => setTimeout(r, 100))
    setLoading(true); setError('')
    try {
      const data = await fetchRows('crew')
      setRows(data)
      const openCrewId = localStorage.getItem('hc_open_crew_id')
      const openCrewTitle = localStorage.getItem('hc_open_crew_title')
      if (openCrewId) {
        localStorage.removeItem('hc_open_crew_id')
        const found = data.find(r => String(r.id) === openCrewId)
        if (found) setDetailCrew(found)
      } else if (openCrewTitle) {
        localStorage.removeItem('hc_open_crew_title')
        const found = data.find(r => {
          const name = (getVal(r['First Name']) + ' ' + getVal(r['Last Name'])).toLowerCase()
          return openCrewTitle.toLowerCase().includes(name) && name.length > 2
        })
        if (found) setDetailCrew(found)
      }
    }
    catch(e) { setError(e.message) }
    finally { setLoading(false) }
  }

  const isArchived = r => r['Archived'] === true || getVal(r['Archived']) === 'true'
  const filtered = rows.filter(r => {
    const name = `${getVal(r['First Name'])} ${getVal(r['Last Name'])}`.toLowerCase()
    const matchQ = !query || name.includes(query.toLowerCase()) || Object.values(r).some(v => String(v).toLowerCase().includes(query.toLowerCase()))
    const matchDept = !deptFilter || getVal(r['Department']) === deptFilter
    const matchOnboard = !onboardFilter || (onboardFilter === 'Onboard' ? isOnboard(r) : !isOnboard(r))
    const matchArchived = showArchived ? isArchived(r) : !isArchived(r)
    return matchQ && matchDept && matchOnboard && matchArchived
  })

  const onboardCount = rows.filter(r => isOnboard(r)).length
  const depts = [...new Set(rows.map(r => getVal(r['Department'])).filter(Boolean))]

  async function loadAccounts() {
    if (!authConfig?.id) return
    setAccLoading(true)
    const { data } = await supabase.from('crew_accounts').select('*').eq('client_id', authConfig.id).order('created_at')
    setAccounts(data || [])
    setAccLoading(false)
  }

  useEffect(() => { if (view === 'accounts') loadAccounts() }, [view])

  async function savePerms(accountId) {
    await supabase.from('crew_accounts').update({ permissions: permForm }).eq('id', accountId)
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, permissions: permForm } : a))
    setPermPanel(null)
  }

  async function saveAccount() {
    setAccSaving(true); setAccError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'create_crew', email: accForm.email, password: accForm.password, name: accForm.name, role: accForm.role || 'crew', client_id: authConfig?.id })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed')
      setAccModal(false); setAccForm({}); loadAccounts()
    } catch(e) { setAccError(e.message) }
    finally { setAccSaving(false) }
  }

  async function deactivateAccount(id) {
    if (!window.confirm('Deactivate this crew account?')) return
    const { data: { session } } = await supabase.auth.getSession()
    await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token }, body: JSON.stringify({ action: 'deactivate_crew', crew_id: id }) })
    loadAccounts()
  }

  function openNew() { setFormData({}); setOrigForm(null); setSaveError(''); setModal('new') }
  function openEdit(row) {
    const fd = {}
    FORM_FIELDS.forEach(f => {
      const v = row[f.name]
      if (!v && v !== 0) { fd[f.name] = ''; return }
      if (typeof v === 'object' && v.value) { fd[f.name] = v.value; return }
      fd[f.name] = v
    })
    setDetailCrew(null)
    setFormData(fd); setOrigForm(fd); setSaveError(''); setModal(row)
  }

  async function handleSave() {
    setSaving(true); setSaveError('')
    const body = formBody(formFields, formData, modal === 'new' ? null : origForm)
    // saved with no change: the window closes quietly, nothing is sent
    if (modal !== 'new' && !Object.keys(body).length) { setSaving(false); setModal(null); return }
    try {
      if (modal === 'new') { const newRow = await createRow('crew', body); setRows(prev => [newRow, ...prev]) }
      else { const updated = await updateRow('crew', modal.id, body); setRows(prev => prev.map(r => r.id === modal.id ? updated : r)) }
      setModal(null)
    } catch(e) { setSaveError('Save failed: ' + e.message) }
    finally { setSaving(false) }
  }

  async function handleArchiveCrew() {
    if (modal === 'new' || !modal) return
    const nm = ((modal['First Name']||'')+' '+(modal['Last Name']||'')).trim() || 'this crew member'
    const currentlyArchived = modal['Archived'] === true || String(modal['Archived']) === 'true'
    const verb = currentlyArchived ? 'Restore' : 'Archive'
    if (!window.confirm(verb + ' ' + nm + '?' + (currentlyArchived ? '' : ' They will be moved to the archive and hidden from the active crew list, but their record is kept.'))) return
    setSaving(true); setSaveError('')
    try {
      const updated = await updateRow('crew', modal.id, { 'Archived': !currentlyArchived })
      setRows(prev => prev.map(r => r.id === modal.id ? updated : r))
      setModal(null)
    } catch(e) { setSaveError((currentlyArchived ? 'Restore' : 'Archive') + ' failed: ' + e.message) }
    finally { setSaving(false) }
  }

  // personal data (GDPR): export as a ZIP, or delete everything in two steps (src/components/CrewDataPanel.jsx) - Captain / management only.
  // It replaces the old "Delete permanently" that removed the crew row only and left their documents, files and records behind.
  function handlePersonalData() {
    if (modal === 'new' || !modal) return
    setDataFor({ id: modal.id, name: ((modal['First Name'] || '') + ' ' + (modal['Last Name'] || '')).trim() || modal['Name'] || '' })
  }

  // the toggle only asks (date + place); SignEventModal does the sign-on / sign-off (src/lib/signEvent.js)
  function handleToggleOnboard(row, currentlyOnboard) { setSignAsk({ row, signingOn: !currentlyOnboard }) }
  function applyCrewRow(updated) {
    if (!updated || !updated.id) return
    setRows(prev => prev.map(r => r.id === updated.id ? updated : r))
    setDetailCrew(d => (d && d.id === updated.id ? updated : d))
  }

  return (
    <>
      <div className="tbl-wrap fade-up">
        <div style={cs.statsBar}>
          <div style={{ ...cs.statChip, cursor: 'pointer', outline: onboardFilter === 'Onboard' ? '1.5px solid #137050' : 'none' }} onClick={() => setOnboardFilter(onboardFilter === 'Onboard' ? '' : 'Onboard')}>
            <span style={{ color: '#137050', fontWeight: 600 }}>{onboardCount}</span>
            <span style={{ color: 'var(--muted)', fontSize: 10 }}>Onboard</span>
          </div>
          <div style={{ ...cs.statChip, cursor: 'pointer', outline: onboardFilter === 'Offboard' ? '1.5px solid #b4313c' : 'none' }} onClick={() => setOnboardFilter(onboardFilter === 'Offboard' ? '' : 'Offboard')}>
            <span style={{ color: '#b4313c', fontWeight: 600 }}>{rows.length - onboardCount}</span>
            <span style={{ color: 'var(--muted)', fontSize: 10 }}>Ashore</span>
          </div>
          <div style={{ ...cs.statChip, cursor: 'pointer', outline: onboardFilter === '' && !showArchived ? '1.5px solid var(--gold2)' : 'none' }} onClick={() => { setOnboardFilter(''); setShowArchived(false) }}>
            <span style={{ color: 'var(--gold2)', fontWeight: 600 }}>{rows.filter(r => !(r['Archived'] === true || String(r['Archived']) === 'true')).length}</span>
            <span style={{ color: 'var(--muted)', fontSize: 10 }}>Total</span>
          </div>
          <div style={{ ...cs.statChip, cursor: 'pointer', outline: showArchived ? '1.5px solid #5b6878' : 'none' }} onClick={() => setShowArchived(v => !v)}>
            <span style={{ color: '#5b6878', fontWeight: 600 }}>{rows.filter(r => r['Archived'] === true || String(r['Archived']) === 'true').length}</span>
            <span style={{ color: 'var(--muted)', fontSize: 10 }}>Archived</span>
          </div>
        </div>

        {(() => {
          const rem = eraseReminder(eraseDue, canManageCrewData(authConfig))
          return rem && (
            <div style={{ margin: '10px 16px 0', padding: '10px 12px', border: '1px solid var(--gold2)', borderRadius: 10, fontSize: 12.5 }}>
              <b>{rem.title}.</b> {rem.ask}{' '}
              {rem.people.map((p, i) => (
                <Fragment key={p.id}>{i > 0 && ', '}<button type="button" onClick={() => setDataFor({ id: p.id, name: p.name })}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#c0392b', textDecoration: 'underline', cursor: 'pointer', fontSize: 12.5 }}>{p.name}</button>
                  {p.leftOn && <span style={{ color: 'var(--muted)' }}> (left {fmtDocDate(p.leftOn)})</span>}</Fragment>
              ))}
            </div>
          )
        })()}

        <style>{`
          @media (max-width: 1023px) {
            .tbl-toolbar { flex-wrap: wrap !important; gap: 8px !important; padding: 10px !important; }
            .tbl-toolbar .search-inp { flex: 1 1 100% !important; width: 100% !important; box-sizing: border-box !important; }
            .tbl-toolbar select { flex: 1 1 45% !important; min-width: 0 !important; max-width: 100% !important; box-sizing: border-box !important; }
            .tbl-toolbar > .btn { flex: 1 1 auto !important; min-width: 92px !important; font-size: 12px !important; }
          }
        `}</style>
        <div className="tbl-toolbar">
          <input className="search-inp" placeholder="Search crew..." value={query} onChange={e => setQuery(e.target.value)} />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={cs.filterSelect}>
            <option value="">All Departments</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={onboardFilter} onChange={e => setOnboardFilter(e.target.value)} style={cs.filterSelect}>
            <option value="">All Status</option>
            <option value="Onboard">Onboard</option>
            <option value="Offboard">Ashore</option>
          </select>
          <div style={cs.viewToggle}>
            <button style={{ ...cs.viewBtn, ...(view==='cards'?cs.viewBtnActive:{}) }} onClick={() => setView('cards')}>Cards</button>
            <button style={{ ...cs.viewBtn, ...(view==='list'?cs.viewBtnActive:{}) }} onClick={() => setView('list')}>List</button>
            {!isCrew && <button style={{ ...cs.viewBtn, ...(view==='accounts'?cs.viewBtnActive:{}) }} onClick={() => setView('accounts')}>Accounts</button>}
          </div>
          <button className="btn" onClick={load} style={{ fontSize: 11 }}>Refresh</button>
          {seeOthers && <button className="btn" onClick={() => setShowFAL(true)} style={{ fontSize: 11 }}>Crew List</button>}
          <button className="btn" onClick={() => onNavigate && onNavigate('crew_certs')} style={{ fontSize: 11 }}>All Certificates</button>
          <button className="btn btn-primary" onClick={openNew}>+ Add Crew</button>
        </div>

        {view === 'accounts' && (
          <div style={{ padding: 16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 16 }}>
              <div style={{ color:'var(--text)', fontWeight:600 }}>Crew Accounts for {authConfig?.vessel_name}</div>
              <button className="btn btn-primary" onClick={() => { setAccForm({ role:'crew' }); setAccError(''); setAccModal(true) }}>+ Add account</button>
            </div>
            {accLoading && <div className="loading-row"><div className="spin" />Loading...</div>}
            {!accLoading && (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {accounts.length === 0
                      ? <tr><td colSpan={6}><div className="no-rows">No crew accounts yet. Add one to get started.</div></td></tr>
                      : accounts.map(a => (
                        <tr key={a.id} className="data-row">
                          <td><strong style={{ color:'var(--gold2)' }}>{a.name}</strong></td>
                          <td>{a.email}</td>
                          <td><span className="pill pill-b">{a.role?.replace('_',' ')}</span></td>
                          <td>{a.active ? <span className="pill pill-g">Active</span> : <span className="pill pill-r">Inactive</span>}</td>
                          <td>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</td>
                          <td style={{ display:'flex', gap:6 }}>
                            <button className="act-btn" onClick={() => { setPermForm(a.permissions||{}); setPermPanel(a.id) }}>Permissions</button>
                            {a.active && <button className="act-btn del" onClick={() => deactivateAccount(a.id)}>Deactivate</button>}
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
            {permPanel && createPortal(
              <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setPermPanel(null)}>
                <div style={{ background:'var(--hull)', borderRadius:12, padding:24, width:620, maxWidth:'95vw', height:'80vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
                  <div style={{ fontWeight:600, color:'var(--text)', marginBottom:4 }}>Permissions — {accounts.find(a=>a.id===permPanel)?.name}</div>
                  <div style={{ fontSize:11, color:'var(--muted)', marginBottom:16 }}>Set access level for each module</div>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ position:'sticky', top:0, background:'var(--hull)', zIndex:1 }}><tr>
                      <th style={{ textAlign:'left', fontSize:11, color:'var(--muted)', paddingBottom:8, paddingTop:4 }}>Module</th>
                      {['None','Read','Write','Full'].map(l=><th key={l} style={{ textAlign:'center', fontSize:11, color:'var(--muted)', paddingBottom:8, paddingTop:4, minWidth:50 }}>{l}</th>)}
                    </tr></thead>
                    <tbody>
                      {[['dashboard','Dashboard'],['ai','AI Assistant'],['announcements','Crew Board'],['equipment','Equipment'],['maintenance','Maintenance'],['inventory','Inventory'],['documents','Documents'],['checklists','Checklists'],['todo','To Do'],['lsa','LSA / FFE'],['risk','Risk Assessments'],['refit','Refit / Yard Period'],['budget','Budget'],['crew','Crew'],['hours_rest','Hours of Rest'],['watch_schedule','Watch Schedule'],['duty_rota','Duty Rota'],['calendar','Calendar'],['charters','Charters'],['port_calls','Port Calls'],['marina','Marina Bookings'],['passage','Passage Planning'],['weather','Weather'],['manuals','Manuals'],['regulations','Regulations'],['officer_review','Monthly Review'],['reports','Reports'],['logbooks','Log Books'],['workspaces','Workspaces']].map(([k,label])=>(
                        <tr key={k} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ color:'var(--text)', fontSize:13, padding:'6px 0' }}>{label}</td>
                          {['none','read','write','full'].map(lv=>(
                            <td key={lv} style={{ textAlign:'center' }}>
                              <input type="radio" name={k} checked={(permForm[k]||'read')===lv} onChange={()=>setPermForm(p=>({...p,[k]:lv}))} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                    <button className="btn" onClick={()=>setPermPanel(null)}>Cancel</button>
                    <button className="btn btn-primary" onClick={()=>savePerms(permPanel)}>Save Permissions</button>
                  </div>
                </div>
              </div>
            , document.body)}

            {accModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setAccModal(false)}>
                <div style={{ background:'var(--hull)', borderRadius:12, padding:24, width:400, maxWidth:'90vw' }} onClick={e => e.stopPropagation()}>
                  <div style={{ fontWeight:600, color:'var(--text)', marginBottom:16 }}>Add Crew Account</div>
                  {['name','email','password'].map(f => (
                    <div key={f} className="field">
                      <label style={{ textTransform:'capitalize' }}>{f === 'password' ? 'Temporary Password' : f}</label>
                      <input type={f==='password'?'password':'text'} placeholder={f==='name'?'John Smith':f==='email'?'john@vessel.com':'Set a password'} value={accForm[f]||''} onChange={e => setAccForm(p=>({...p,[f]:e.target.value}))} />
                    </div>
                  ))}
                  <div className="field">
                    <label>Role</label>
                    <select value={accForm.role||'crew'} onChange={e => setAccForm(p=>({...p,role:e.target.value}))}>
                      {['captain','chief_officer','engineer','steward','crew'].map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  {accError && <div style={{ color:'var(--danger)', fontSize:12, marginBottom:8 }}>{accError}</div>}
                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                    <button className="btn" onClick={() => setAccModal(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={saveAccount} disabled={accSaving}>{accSaving ? 'Creating...' : 'Create Account'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && <div className="loading-row"><div className="spin" />Loading crew...</div>}
        {error && <div className="err-box" style={{ margin: 12 }}>{error}</div>}

        {!loading && !error && view === 'cards' && (
          <div style={cs.cardGrid}>
            {filtered.length === 0
              ? <div style={{ gridColumn: '1/-1', padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>No crew members found.</div>
              : filtered.map(row => (
                <CrewCard key={row.id} crew={row} onEdit={openEdit} onToggleOnboard={handleToggleOnboard} onClick={() => setDetailCrew(row)} />
              ))
            }
          </div>
        )}

        {!loading && !error && view === 'list' && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr>
                <th>Name</th><th>Position</th><th>Department</th><th>Nationality</th>
                <th>Phone</th><th>Contract End</th><th>Passport Exp.</th><th>Docs</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan={10}><div className="no-rows">No crew members found.</div></td></tr>
                  : filtered.map(row => {
                    const name = `${getVal(row['First Name'])} ${getVal(row['Last Name'])}`
                    const ob = isOnboard(row)
                    const ds = docStatus(row)
                    return (
                      <tr key={row.id} className="data-row" onClick={() => setDetailCrew(row)} style={{ cursor: 'pointer' }}>
                        <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={name} photo={getVal(row['Photo URL'])} size={28} />{name}</div></td>
                        <td>{getVal(row['Position'])}</td>
                        <td>{getVal(row['Department'])}</td>
                        <td>{getVal(row['Nationality'])}</td>
                        <td>{getVal(row['Phone'])}</td>
                        <td style={{ color: dateColor(getVal(row['Contract End Date'])) }}>{getVal(row['Contract End Date'])}</td>
                        <td style={{ color: dateColor(getVal(row['Passport Expiry Date'])) }}>{getVal(row['Passport Expiry Date'])}</td>
                        <td><span style={{ fontSize: 10, fontWeight: 600, color: ds === 'ok' ? '#137050' : ds === 'expiring' ? 'var(--gold2)' : '#b4313c' }}>{ds === 'ok' ? '✓ OK' : ds === 'expiring' ? '⚠ Soon' : '✕ Expired'}</span></td>
                        <td><span style={{ fontSize: 11, color: ob ? '#137050' : '#b4313c' }}>{ob ? '● Onboard' : '○ Ashore'}</span></td>
                        <td onClick={e => e.stopPropagation()}><button className="act-btn" onClick={() => openEdit(row)}>Edit</button></td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div style={{ padding: '8px 14px', fontSize: 10, color: 'var(--muted)', borderTop: '0.5px solid var(--border)', background: 'var(--hull)' }}>
            {filtered.length} of {rows.length} crew members
          </div>
        )}
      </div>

      {detailCrew && (
        <DetailPanel
          crew={detailCrew}
          onClose={() => setDetailCrew(null)}
          onEdit={openEdit}
          onToggleOnboard={handleToggleOnboard}
          onCrewChanged={applyCrewRow}
        />
      )}
      {signAsk && <SignEventModal crew={signAsk.row} signingOn={signAsk.signingOn} onClose={() => setSignAsk(null)} onCrewChanged={applyCrewRow} />}

      {modal && (
        <Modal title={modal === 'new' ? 'Add Crew Member' : 'Edit Crew Member'} onClose={() => setModal(null)} onSave={handleSave} saving={saving} error={saveError}>
          {modal !== 'new' && modal?.id && <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 6 }}><HistoryLink table="crew" rowId={modal.id} /></div>}
          {formFields.map(f => (
            <div key={f.name} className="field">
              <label>{f.label}{f.required ? ' *' : ''}</label>
              {f.type === 'select'
                ? <select value={formData[f.name] || ''} onChange={e => setFormData(p => ({ ...p, [f.name]: e.target.value }))}><option value="">Select...</option>{f.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
                : f.type === 'textarea'
                ? <textarea rows={3} placeholder={f.placeholder || ''} value={formData[f.name] || ''} onChange={e => setFormData(p => ({ ...p, [f.name]: e.target.value }))} />
                : f.type === 'date'
                ? <DateField value={formData[f.name] || ''} onChange={v => setFormData(p => ({ ...p, [f.name]: v || '' }))} />
                : <input type={f.type || 'text'} placeholder={f.placeholder || ''} value={formData[f.name] || ''} onChange={e => setFormData(p => ({ ...p, [f.name]: e.target.value }))} />
              }
            </div>
          ))}
          {modal !== 'new' && (
            <div style={{ marginTop: 8, paddingTop: 12, borderTop: '0.5px solid var(--border)' }}>
              <button type="button" onClick={handleArchiveCrew} disabled={saving} style={{ background: 'none', border: 'none', color: '#b4313c', fontSize: 12, cursor: 'pointer', padding: 0 }}>{(modal['Archived'] === true || String(modal['Archived']) === 'true') ? 'Restore crew member' : 'Archive crew member'}</button>
              {canManageCrewData(authConfig) && <button type="button" onClick={handlePersonalData} disabled={saving} style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: 12, cursor: 'pointer', padding: 0, marginLeft: 16, textDecoration: 'underline' }}>Personal data: export / erase</button>}
            </div>
          )}
        </Modal>
      )}
      {/* after an erase the crew row stays (archived): the vessel records point at it */}
      {dataFor && (
        <CrewDataPanel crewId={dataFor.id} crewName={dataFor.name} onClose={() => setDataFor(null)}
          onDeleted={() => { setModal(null); load(); loadEraseDue() }} />
      )}
      {showFAL && seeOthers && (
        <FALModal
          rows={rows}
          falFilter={falFilter}
          setFalFilter={setFalFilter}
          portDep={portDep} setPortDep={setPortDep}
          portArr={portArr} setPortArr={setPortArr}
          dateDep={dateDep} setDateDep={setDateDep}
          dateArr={dateArr} setDateArr={setDateArr}
          config={config}
          onClose={() => setShowFAL(false)}
        />
      )}

    </>
  )
}

const cs = {
  statsBar: { display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '0.5px solid var(--border)', background: 'var(--hull)' },
  statChip: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 16px', borderRadius: 8, background: 'var(--chip)', border: '0.5px solid var(--border)', fontSize: 15, fontWeight: 600 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, padding: 16 },
  card: { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, overflow: 'hidden', transition: 'border-color .15s', cursor: 'pointer' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 14px 10px', borderBottom: '0.5px solid var(--border)' },
  crewName: { fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  crewPos: { fontSize: 11, color: 'var(--muted)', marginBottom: 3 },
  deptBadge: { fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 8, letterSpacing: '.04em', display: 'inline-block' },
  cardBody: { padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5 },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5 },
  infoLabel: { color: 'var(--muted)' },
  infoVal: { color: 'var(--text)', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardFooter: { display: 'flex', gap: 6, padding: '8px 14px', borderTop: '0.5px solid var(--border)', background: 'var(--ink2)' },
  filterSelect: { background: 'var(--hull)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' },
  viewToggle: { display: 'flex', border: '0.5px solid var(--border)', borderRadius: 6, overflow: 'hidden' },
  viewBtn: { background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 11, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit' },
  viewBtnActive: { background: 'var(--chip)', color: 'var(--gold2)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' },
  panel: { width: 420, background: 'var(--ink2)', borderLeft: '0.5px solid var(--border)', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  panelHeader: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 20px 16px', borderBottom: '0.5px solid var(--border)', background: 'var(--hull)', flexShrink: 0 },
  panelBody: { flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionTitle: { fontSize: 9, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid var(--border)' },
  detailLabel: { color: 'var(--muted)', flexShrink: 0 },
  detailVal: { color: 'var(--text)', textAlign: 'right', maxWidth: '60%' },
  closeBtn: { background: 'transparent', border: '0.5px solid var(--border)', color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' },
  tabBtn: { background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 12, padding: '10px 16px', cursor: 'pointer', fontFamily: 'inherit', borderBottom: '2px solid transparent' },
  tabActive: { color: 'var(--gold2)', borderBottom: '2px solid var(--gold2)' },
}
